import type { GetStaticProps, InferGetStaticPropsType, NextPage } from "next";
import styles from "./navigation-map.module.scss";
import React from "react";
import { useGoBackHistory } from "app/hooks/go-back.hook";
import { useTranslate } from "app/hooks/lang";
import Loader from "app/components/icons/Loader";
import { RegionCountries } from "app/components/region-countries/region-countries.component";
import { useRouter } from "next/router";
import { Category } from "services-hooks/types";
import {
  getPillowCategoriesPath,
  getPillowCategoryPath,
} from "services-hooks/services";
import { getCircularReplacer } from "app/utils/array";
import { NextSeo } from "next-seo";
import { getServersideLocale } from "app/utils/getServersideLocale";

export const getStaticProps: GetStaticProps<{
  initialData: { region: Category; countries: Category[] }[];
}> = async (context) => {
  try {
    const countryData = await getPillowCategoriesPath(
      "/radioline_geo",
      {
        pageSize: 100,
      },
      {
        headers: {
          authorization: `Bearer ${process.env.SERVER_API_KEY}`,
          "X-PillowLocale": getServersideLocale(context.locale),
        },
      }
    );
    const initialData = await Promise.all(
      countryData.data?.body?.content?.map(async (countryRegion) => ({
        region: countryRegion,
        countries:
          (
            await getPillowCategoryPath(
              countryRegion.permalink!,
              {
                pageSize: 100,
              },
              {
                headers: {
                  authorization: `Bearer ${process.env.SERVER_API_KEY}`,
                  "X-PillowLocale": getServersideLocale(context.locale),
                },
              }
            )
          ).data.body?.content || [],
      })) || []
    );
    return {
      props: {
        initialData: JSON.parse(
          JSON.stringify(initialData, getCircularReplacer())
        ),
      },
      revalidate: 43200,
    };
  } catch (e) {
    console.error(e);
    return {
      notFound: true,
    };
  }
};
export function Home({
  initialData,
}: InferGetStaticPropsType<typeof getStaticProps>) {
  const t = useTranslate();
  const { isFallback } = useRouter();
  useGoBackHistory("INDEX");
  return (
    <>
      <NextSeo
        titleTemplate={`%s`}
        title={t("seo.navigation-map-index-title")}
        description={t("seo.navigation-map-index-description")}
        openGraph={{
          images: [
            {
              url: "https://www.radioline.co/open-graph.png",
              alt: "Radioline",
            },
          ],
        }}
      />
      <div className={styles.container}>
        <div className={styles.countries_wrapper}>
          {isFallback ? (
            <div className={styles.loader_container}>
              <Loader />
            </div>
          ) : (
            <>
              {initialData.map((region) => {
                return (
                  <RegionCountries
                    key={`country-region-${region.region.permalink}`}
                    regionPermalink={region.region.permalink!}
                    countries={region.countries}
                    label={region.region.name}
                    showNames
                  />
                );
              })}
            </>
          )}
        </div>
      </div>
    </>
  );
}

export default Home;
